import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid';
import { PageConfig } from '../../api/page-config';
import { EditPageConfigDialog, useEditPageConfigDialog } from './EditPageConfigDialog';
import { EnumPage, EnumPageMap } from '../../data/PageMap';
import { useGlobal } from '../../units/global/GlobalProvider';
import { grey } from '@mui/material/colors';

export const usePageManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const { pageConfigs, reloadPageConfigs } = useGlobal();
    const sideBar = useSideBar();
    const editPageConfigDialog = useEditPageConfigDialog({ ...page });
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'pageConfigId', headerName: 'pageConfigId', flex: 3, minWidth: 90, cellClassName: 'cell', hide: true },
        {
            field: 'componentName',
            headerName: '頁面元件',
            flex: 9,
            minWidth: 270,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as PageConfig;
                return <>{`${EnumPageMap[row.componentName as EnumPage]?.descr}【${EnumPageMap[row.componentName as EnumPage]?.key}】`}</>;
            },

            hide: false,
        },
        { field: 'path', headerName: '轉跳路徑', flex: 5, minWidth: 150, cellClassName: 'cell', hide: false },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>操作</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as PageConfig;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editPageConfigDialog.setPageConfigId(row.pageConfigId || '');
                                editPageConfigDialog.setEnumPage(EnumPageMap[row.componentName as EnumPage]?.key);
                                editPageConfigDialog.setInputPath(row.path || '');
                                editPageConfigDialog.setOriginUserGroups(row.userGroups || []);
                                editPageConfigDialog.onOpen();
                            }}
                        >
                            編輯
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.pageConfig
                                            .remove({ pageConfigId: row.pageConfigId! })
                                            .finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadPageConfigs();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            刪除
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadPageConfigs();
    }, []);
    return { page, classes, sideBar, editPageConfigDialog, inputSearchConfig, setInputSearchConfig, pageConfigs, columns };
};

export const PageManagePage = (): React.ReactElement => {
    const { page, classes, sideBar, editPageConfigDialog, inputSearchConfig, setInputSearchConfig, pageConfigs, columns } =
        usePageManagePage();
    return (
        <Page {...page} title='頁面管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={inputSearchConfig}
                                onChange={(e) => setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['頁面元件', '轉跳路徑'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => editPageConfigDialog.onOpen()}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增頁面'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return pageConfigs.filter((v, i, a) =>
                                    [
                                        searchs.length == 0
                                            ? true
                                            : searchs
                                                  .map(
                                                      (s) =>
                                                          EnumPageMap[v.componentName as EnumPage]?.key
                                                              .toUpperCase()
                                                              .includes(s.toUpperCase()) ||
                                                          EnumPageMap[v.componentName as EnumPage]?.descr
                                                              .toUpperCase()
                                                              .includes(s.toUpperCase()) ||
                                                          v.path?.toUpperCase().includes(s.toUpperCase()),
                                                  )
                                                  .every((v) => v),
                                    ].every((v) => v),
                                );
                            })()}
                            columns={columns}
                            getRowId={(row: PageConfig) => row.pageConfigId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditPageConfigDialog {...editPageConfigDialog} />
        </Page>
    );
};
